import { useState, type FormEvent } from 'react';
import type { Product, ProductInput } from '~shared/schemas';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '~/hooks/use-products';
import { ApiError } from '~/lib/api';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';

type FormState = {
  name: string;
  price: string;
  originalPrice: string;
  category: string;
  image: string;
  description: string;
  badge: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  price: '',
  originalPrice: '',
  category: '',
  image: '',
  description: '',
  badge: '',
};

function productToForm(p: Product): FormState {
  return {
    name: p.name,
    price: String(p.price),
    originalPrice: p.originalPrice != null ? String(p.originalPrice) : '',
    category: p.category,
    image: p.image,
    description: p.description,
    badge: p.badge ?? '',
  };
}

function formToInput(form: FormState): ProductInput | null {
  const name = form.name.trim();
  const category = form.category.trim();
  const image = form.image.trim();
  const description = form.description.trim();
  const price = Number(form.price);

  if (!name || !category || !image || !Number.isFinite(price) || price <= 0) return null;

  const originalPrice = form.originalPrice.trim() ? Number(form.originalPrice) : null;
  if (originalPrice != null && (!Number.isFinite(originalPrice) || originalPrice <= 0)) return null;

  return {
    name,
    price,
    originalPrice,
    category,
    image,
    description,
    badge: form.badge.trim() || null,
  };
}

function formatPrice(price: number) {
  return price.toLocaleString('ru-RU') + ' ₽';
}

function FormFields({ form, onChange }: { form: FormState; onChange: (form: FormState) => void }) {
  const inputClass = 'w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-slate-600';

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <input
        placeholder="Название"
        value={form.name}
        onChange={e => onChange({ ...form, name: e.target.value })}
        className={inputClass}
        required
      />
      <input
        placeholder="Категория"
        value={form.category}
        onChange={e => onChange({ ...form, category: e.target.value })}
        className={inputClass}
        required
      />
      <input
        placeholder="Цена"
        type="number"
        value={form.price}
        onChange={e => onChange({ ...form, price: e.target.value })}
        className={inputClass}
        required
      />
      <input
        placeholder="Старая цена (если есть скидка)"
        type="number"
        value={form.originalPrice}
        onChange={e => onChange({ ...form, originalPrice: e.target.value })}
        className={inputClass}
      />
      <input
        placeholder="Эмодзи-изображение"
        value={form.image}
        onChange={e => onChange({ ...form, image: e.target.value })}
        className={inputClass}
        required
      />
      <input
        placeholder="Бейдж (Хит, Новинка…)"
        value={form.badge}
        onChange={e => onChange({ ...form, badge: e.target.value })}
        className={inputClass}
      />
      <textarea
        placeholder="Описание"
        value={form.description}
        onChange={e => onChange({ ...form, description: e.target.value })}
        className={`${inputClass} h-20 py-2 resize-none sm:col-span-2`}
      />
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `Ошибка ${err.status}`;
  }
  return err instanceof Error ? err.message : 'Неизвестная ошибка';
}

function AddProductForm() {
  const create = useCreateProduct();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [validationError, setValidationError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const input = formToInput(form);
    if (!input) {
      setValidationError('Заполни обязательные поля: название, категория, изображение, цена больше нуля');
      return;
    }
    setValidationError(null);
    await create.mutateAsync(input);
    setForm(EMPTY_FORM);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Добавить товар</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-3">
          <FormFields form={form} onChange={setForm} />
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Добавляю…' : 'Добавить'}
            </Button>
            {validationError && (
              <span className="text-sm text-red-600 dark:text-red-400">{validationError}</span>
            )}
            {create.error && !validationError && (
              <span className="text-sm text-red-600 dark:text-red-400">{extractErrorMessage(create.error)}</span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EditProductModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const update = useUpdateProduct();
  const [form, setForm] = useState<FormState>(() => productToForm(product));
  const [validationError, setValidationError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const input = formToInput(form);
    if (!input) {
      setValidationError('Заполни обязательные поля: название, категория, изображение, цена больше нуля');
      return;
    }
    setValidationError(null);
    await update.mutateAsync({ id: product.id, input });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Редактировать товар #{product.id}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <FormFields form={form} onChange={setForm} />
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Сохраняю…' : 'Сохранить'}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Отмена
              </Button>
              {validationError && (
                <span className="text-sm text-red-600 dark:text-red-400">{validationError}</span>
              )}
              {update.error && !validationError && (
                <span className="text-sm text-red-600 dark:text-red-400">{extractErrorMessage(update.error)}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminPage() {
  const products = useProducts();
  const deleteProduct = useDeleteProduct();
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  function handleDelete(product: Product) {
    if (!window.confirm(`Удалить товар «${product.name}»?`)) return;
    deleteProduct.mutate(product.id);
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 dark:bg-slate-950">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Админка каталога</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Управление товарами: добавление, редактирование, удаление
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Товары</CardTitle>
          </CardHeader>
          <CardContent>
            {products.isPending && (
              <div className="text-sm text-slate-500 dark:text-slate-400">Загрузка…</div>
            )}
            {products.error && (
              <div className="text-sm text-red-600 dark:text-red-400">
                Ошибка: {extractErrorMessage(products.error)}
              </div>
            )}
            {products.data && products.data.length === 0 && (
              <div className="text-sm text-slate-500 dark:text-slate-400">Товаров пока нет.</div>
            )}
            {products.data && products.data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs text-slate-400 dark:border-slate-800 dark:text-slate-500">
                      <th className="py-2 pr-3 font-medium"></th>
                      <th className="py-2 pr-3 font-medium">Название</th>
                      <th className="py-2 pr-3 font-medium">Категория</th>
                      <th className="py-2 pr-3 font-medium">Цена</th>
                      <th className="py-2 pr-3 font-medium">Бейдж</th>
                      <th className="py-2 pr-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.data.map(product => (
                      <tr key={product.id} className="border-b border-slate-100 last:border-0 dark:border-slate-900">
                        <td className="py-2 pr-3 text-xl">{product.image}</td>
                        <td className="py-2 pr-3 font-medium text-slate-900 dark:text-slate-100">{product.name}</td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{product.category}</td>
                        <td className="py-2 pr-3">
                          {product.originalPrice && (
                            <span className="mr-1 text-slate-400 line-through dark:text-slate-500">
                              {formatPrice(product.originalPrice)}
                            </span>
                          )}
                          <span className="text-slate-900 dark:text-slate-100">{formatPrice(product.price)}</span>
                        </td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-slate-400">{product.badge ?? '—'}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setEditingProduct(product)}>
                              Редактировать
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(product)}>
                              Удалить
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <AddProductForm />
      </div>

      {editingProduct && (
        <EditProductModal product={editingProduct} onClose={() => setEditingProduct(null)} />
      )}
    </div>
  );
}
